import React, { useEffect, useRef } from 'react';
import doctor from '../../assets/doctor.png';
import { useNavigate } from 'react-router';

const DentistrySection = () => {
  const badgeRef = useRef(null);
  const navigate = useNavigate();

  // Simple animation for the badge
  useEffect(() => {
    const badge = badgeRef.current;
    if (!badge) return;

    let direction = 1;
    let position = 0;
    const animate = () => {
      if (position > 10) direction = -1;
      if (position < 0) direction = 1;
      position += 0.1 * direction;
      badge.style.transform = `rotate(15deg) translateY(${position}px)`;
      requestAnimationFrame(animate);
    };

    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="bg-gradient-to-br from-teal-500 to-teal-600 min-h-screen p-4 md:p-8" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }}>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl relative overflow-hidden">
          {/* Decorative elements matching the logo style */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-lime-400 opacity-20 rounded-full transform -translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-teal-400 opacity-20 rounded-full transform translate-x-20 translate-y-20"></div>

          <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
            {/* Left Content Section */}
            <div className="text-gray-700 space-y-6">
              <h1 className="text-4xl md:text-5xl font-serif mb-6 text-teal-600">
                Dentistry Done Right
              </h1>

              <p className="text-lg mb-8">
                Adults and kids, we welcome patients from 3 years old to seniors! Our team is passionate about building lifetime relationships through positive experiences, featuring:
              </p>

              <ul className="space-y-4">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-lime-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  <span>Transparent Pricing</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-lime-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  <span>Unparalleled Warranty</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-lime-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  <span>10% Off Every Smile Upgrade</span>
                </li>
              </ul>

              <blockquote className="border-l-4 border-teal-500 pl-4 mt-8">
                <p className="italic text-teal-700">
                  "Our word is our worth. We promise to do it right, timely, and for a fair price."
                </p>
              </blockquote>

              <button onClick={() => navigate('/schedule-appointment')} className="cursor-pointer mt-6 bg-teal-600 hover:bg-teal-700 text-white py-3 px-6 rounded-full transition duration-300 transform hover:scale-105 shadow-lg">
                Schedule Your Visit
              </button>
            </div>

            {/* Right Image Section */}
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-50 to-white p-6 rounded-2xl shadow-lg border border-teal-100">
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-lime-400 rounded-full opacity-70"></div>
                <div className="flex items-center justify-center mb-4 relative">
                  <img src={doctor} alt="Pristine Smile Dentist" className="rounded-lg relative z-10" />
                  {/* SVG pattern overlay */}
                  <div className="absolute inset-0 rounded-lg overflow-hidden opacity-10 z-0">
                    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#teeth-pattern)" />
                      <defs>
                        <pattern id="teeth-pattern" patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
                          <path d="M10,2 C12,6 15,6 15,10 C15,14 12,14 10,18 C8,14 5,14 5,10 C5,6 8,6 10,2 Z" fill="#059669" />
                        </pattern>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warranty Badge */}
          <div ref={badgeRef} className="hidden sm:flex absolute bottom-4 right-4 md:bottom-8 md:right-8" style={{ zIndex: 10 }}>
            <div className="bg-gradient-to-br from-lime-400 to-teal-500 rounded-full p-4 w-32 h-32 flex flex-col items-center justify-center text-center text-white shadow-lg transform rotate-12">
              <span className="font-bold text-sm">Smile Guarantee</span>
              <div className="w-8 h-8 my-1 rounded-full flex items-center justify-center">
                <span>😊</span>
              </div>
              <span className="text-xs">We stand behind your smile — always.</span>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DentistrySection;